import { RootState } from 'store';
import { createSelector } from '@reduxjs/toolkit';
import { OptionsState } from './interfaces';

export const selectOptions = (state: RootState) => state.options;

export const selectOptionsForDepartmentOverviewPage = createSelector(
  (state: RootState) => state.options,
  (options: OptionsState) => ({
    ...options,
    units: options.units.filter(({ name }) => name !== 'PP'),
  }),
);

export const selectDefaultAssessmentScaleGroupId = (state: RootState) =>
  state.options.assessmentScaleGroups.find(({ alias }) => alias === 'general')
    ?.id ||
  state.options.assessmentScaleGroups.find(({ alias }) => alias === 'standard')
    ?.id ||
  '';

export const selectAssessmentScaleGroups = (state: RootState) =>
  state.options.assessmentScaleGroups;

export const selectAssessmentScales = (state: RootState) =>
  state.options.assessmentScales;

export const selectAllUnits = (state: RootState) => state.options.units;

export const selectUserScopedUnits = (state: RootState) =>
  state.options.userScopedUnits;

export const selectUserScopedLocations = (state: RootState) =>
  state.options.userScopedLocations;

export const selectUserScopedReviewCycles = (state: RootState) =>
  state.options.userScopedReviewCycles;

export const selectAllReviewCycles = (state: RootState) =>
  state.options.reviewCycles;

export const selectUsersOptions = (state: RootState) => state.options.users;

export const selectHierarchicalUnitsOptions = createSelector(
  [selectAllUnits],
  (units) => {
    if (!units || units.length === 0) return [];

    const unitsMap = new Map(units.map((unit) => [unit.id, unit]));

    const rootUnits = units.filter(
      (unit) => !unit.parentId || !unitsMap.has(unit.parentId),
    );

    const sortedRootUnits = rootUnits.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );

    const result: typeof units = [];

    const addUnitAndChildren = (unit: (typeof units)[0]) => {
      result.push(unit);

      const children = units
        .filter((child) => child.parentId === unit.id)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        );

      children.forEach((child) => addUnitAndChildren(child));
    };

    sortedRootUnits.forEach((rootUnit) => {
      addUnitAndChildren(rootUnit);
    });

    return result;
  },
);
